import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  SelectChangeEvent,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import { patientService, PatientHealth } from "../../services/patient.service";
import { toLocaleDateString } from "../../utils/dateUtils";

interface Props {
  patientId: number;
}

interface TestRate {
  rate: number;
  frequency: string;
}

const parseTestRate = (rateString: string | undefined): TestRate => {
  if (!rateString) return { rate: 0, frequency: "D" };
  const parts = rateString.split("|");
  return {
    rate: parseInt(parts[0]) || 0,
    frequency: parts[1] || "D",
  };
};

const formatTestRate = (rate: number, frequency: string): string => {
  return `${rate}|${frequency}`;
};

const PatientHealthTab: React.FC<Props> = ({ patientId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [health, setHealth] = useState<PatientHealth | null>(null);

  // Test rates state
  const [glRate, setGlRate] = useState<TestRate>({ rate: 0, frequency: "D" });
  const [bpRate, setBpRate] = useState<TestRate>({ rate: 0, frequency: "D" });
  const [wsRate, setWsRate] = useState<TestRate>({ rate: 0, frequency: "D" });
  const [tmRate, setTmRate] = useState<TestRate>({ rate: 0, frequency: "D" });
  const [poRate, setPoRate] = useState<TestRate>({ rate: 0, frequency: "D" });
  const [atRate, setAtRate] = useState<TestRate>({ rate: 0, frequency: "D" });

  const loadHealth = useCallback(async () => {
    try {
      setLoading(true);
      const data = await patientService.getPatientHealth(patientId);
      setHealth(data);

      // Parse test rates
      setGlRate(parseTestRate(data.glTestRate));
      setBpRate(parseTestRate(data.bpTestRate));
      setWsRate(parseTestRate(data.wsTestRate));
      setTmRate(parseTestRate(data.tmTestRate));
      setPoRate(parseTestRate(data.poTestRate));
      setAtRate(parseTestRate(data.atTestRate));
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load health settings");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  const handleSave = async () => {
    if (!health) return;

    try {
      setSaving(true);
      setSuccess(false);
      setError("");

      // Format test rates before saving
      const updatedHealth = {
        ...health,
        glTestRate: formatTestRate(glRate.rate, glRate.frequency),
        bpTestRate: formatTestRate(bpRate.rate, bpRate.frequency),
        wsTestRate: formatTestRate(wsRate.rate, wsRate.frequency),
        tmTestRate: formatTestRate(tmRate.rate, tmRate.frequency),
        poTestRate: formatTestRate(poRate.rate, poRate.frequency),
        atTestRate: formatTestRate(atRate.rate, atRate.frequency),
      };

      await patientService.updatePatientHealth(patientId, updatedHealth);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update health settings");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof PatientHealth, value: any) => {
    if (!health) return;
    setHealth({ ...health, [field]: value });
  };

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!health) {
    return <Alert severity='error'>{error || "No data found"}</Alert>;
  }

  return (
    <Box>
      {success && (
        <Alert severity='success' sx={{ mb: 2 }}>
          Health settings updated successfully
        </Alert>
      )}
      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* General Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant='h6' gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          General
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!health.diabetic}
                  onChange={(e) => handleChange("diabetic", e.target.checked)}
                />
              }
              label='Diabetic'
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Type Diabetes</InputLabel>
              <Select
                value={health.typeDiabetes || 0}
                onChange={(e) =>
                  handleChange(
                    "typeDiabetes",
                    parseInt(e.target.value as string),
                  )
                }
                label='Type Diabetes'
              >
                <MenuItem value={0}>None or Not Indicated</MenuItem>
                <MenuItem value={1}>Type 1</MenuItem>
                <MenuItem value={2}>Type 2</MenuItem>
                <MenuItem value={3}>Gestational</MenuItem>
                <MenuItem value={4}>Pre Diabetes</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!health.insulinDependent}
                  onChange={(e) =>
                    handleChange("insulinDependent", e.target.checked)
                  }
                />
              }
              label='Insulin Dependent'
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant='subtitle2' gutterBottom>
              General Conditions
            </Typography>
            {health.conditionsList && health.conditionsList.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {health.conditionsList.map(
                  (condition: { id: number; name: string }) => (
                    <Chip
                      key={condition.id}
                      label={condition.name}
                      size='small'
                      variant='outlined'
                    />
                  ),
                )}
              </Box>
            ) : (
              <Typography variant='body2' color='text.secondary'>
                No conditions recorded
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Prescribed Test Rates Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant='h6' gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          Prescribed Test Rates
        </Typography>

        <Grid container spacing={3}>
          {/* Blood Glucose */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant='body2' gutterBottom fontWeight={500}>
              Blood Glucose
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <FormControl sx={{ width: 100 }}>
                <Select
                  size='small'
                  value={glRate.rate}
                  onChange={(e: SelectChangeEvent<number>) =>
                    setGlRate({ ...glRate, rate: Number(e.target.value) })
                  }
                >
                  {Array.from({ length: 13 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {i}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <Select
                  size='small'
                  value={glRate.frequency}
                  onChange={(e) =>
                    setGlRate({ ...glRate, frequency: e.target.value })
                  }
                >
                  <MenuItem value='D'>Daily</MenuItem>
                  <MenuItem value='W'>Weekly</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {/* Blood Pressure */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant='body2' gutterBottom fontWeight={500}>
              Blood Pressure
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <FormControl sx={{ width: 100 }}>
                <Select
                  size='small'
                  value={bpRate.rate}
                  onChange={(e: SelectChangeEvent<number>) =>
                    setBpRate({ ...bpRate, rate: Number(e.target.value) })
                  }
                >
                  {Array.from({ length: 13 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {i}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <Select
                  size='small'
                  value={bpRate.frequency}
                  onChange={(e) =>
                    setBpRate({ ...bpRate, frequency: e.target.value })
                  }
                >
                  <MenuItem value='D'>Daily</MenuItem>
                  <MenuItem value='W'>Weekly</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {/* Weight */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant='body2' gutterBottom fontWeight={500}>
              Weight
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <FormControl sx={{ width: 100 }}>
                <Select
                  size='small'
                  value={wsRate.rate}
                  onChange={(e: SelectChangeEvent<number>) =>
                    setWsRate({ ...wsRate, rate: Number(e.target.value) })
                  }
                >
                  {Array.from({ length: 13 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {i}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <Select
                  size='small'
                  value={wsRate.frequency}
                  onChange={(e) =>
                    setWsRate({ ...wsRate, frequency: e.target.value })
                  }
                >
                  <MenuItem value='D'>Daily</MenuItem>
                  <MenuItem value='W'>Weekly</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {/* Temperature */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant='body2' gutterBottom fontWeight={500}>
              Temperature
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <FormControl sx={{ width: 100 }}>
                <Select
                  size='small'
                  value={tmRate.rate}
                  onChange={(e: SelectChangeEvent<number>) =>
                    setTmRate({ ...tmRate, rate: Number(e.target.value) })
                  }
                >
                  {Array.from({ length: 13 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {i}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <Select
                  size='small'
                  value={tmRate.frequency}
                  onChange={(e) =>
                    setTmRate({ ...tmRate, frequency: e.target.value })
                  }
                >
                  <MenuItem value='D'>Daily</MenuItem>
                  <MenuItem value='W'>Weekly</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {/* Blood Oxygen */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant='body2' gutterBottom fontWeight={500}>
              Blood Oxygen
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <FormControl sx={{ width: 100 }}>
                <Select
                  size='small'
                  value={poRate.rate}
                  onChange={(e: SelectChangeEvent<number>) =>
                    setPoRate({ ...poRate, rate: Number(e.target.value) })
                  }
                >
                  {Array.from({ length: 13 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {i}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <Select
                  size='small'
                  value={poRate.frequency}
                  onChange={(e) =>
                    setPoRate({ ...poRate, frequency: e.target.value })
                  }
                >
                  <MenuItem value='D'>Daily</MenuItem>
                  <MenuItem value='W'>Weekly</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {/* Activity */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant='body2' gutterBottom fontWeight={500}>
              Activity
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <FormControl sx={{ width: 100 }}>
                <Select
                  size='small'
                  value={atRate.rate}
                  onChange={(e: SelectChangeEvent<number>) =>
                    setAtRate({ ...atRate, rate: Number(e.target.value) })
                  }
                >
                  {Array.from({ length: 13 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {i}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <Select
                  size='small'
                  value={atRate.frequency}
                  onChange={(e) =>
                    setAtRate({ ...atRate, frequency: e.target.value })
                  }
                >
                  <MenuItem value='D'>Daily</MenuItem>
                  <MenuItem value='W'>Weekly</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Chronic Diagnosis Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant='h6' gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
          Chronic Diagnosis
        </Typography>

        {health.diagnosis && health.diagnosis.length > 0 ? (
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>ICD-10 Code</strong>
                </TableCell>
                <TableCell>
                  <strong>Diagnosis</strong>
                </TableCell>
                <TableCell>
                  <strong>Start Date</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {health.diagnosis.map(
                (
                  diag: {
                    icd10Code: string;
                    title: string;
                    startDate: string;
                  },
                  index: number,
                ) => (
                  <TableRow key={index}>
                    <TableCell>{diag.icd10Code}</TableCell>
                    <TableCell>{diag.title}</TableCell>
                    <TableCell>
                      {diag.startDate
                        ? toLocaleDateString(diag.startDate)
                        : "—"}
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            No chronic diagnosis recorded
          </Typography>
        )}
      </Paper>

      {/* Save Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button
          variant='contained'
          size='large'
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Box>
    </Box>
  );
};

export default PatientHealthTab;
